import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Room,
  RoomEvent,
  Participant,
  ParticipantEvent,
  ParticipantKind,
  Track,
  RemoteTrackPublication,
  RemoteParticipant,
} from 'livekit-client';
import {
  CampaignTestCallCreateRequestDto,
  useCampaignTestCallCreateControllerTestCallCreateMutation,
} from '@shared/services/apiService';
import { VITE_LIVEKIT_HOST } from '@shared/constants/environments';
import { CALLER_AUDIO_CAPTURE } from '@shared/constants/audioCapture';
import { useTranslation } from 'react-i18next';
import { resolveWebSocketUrl } from '@shared/utils/serviceUrl';
import { toast } from 'react-toastify';
import { CustomErrorToast } from '@shared/components/customErrorToast';

import {
  PARTICIPANT_IDENTITIES,
  CALL_TYPES,
  INTERNAL_CALL_TYPES,
  TIMEOUTS,
  ROOM_STATES,
  DEFAULT_LIVEKIT_HOST,
  DEFAULT_SIP_STATE,
  DEFAULT_PARTICIPANT_STATES,
} from '../constants';
import {
  CallType,
  InternalCallType,
  WebCallResponse,
  SipParticipantState,
  AllParticipantStates,
  UseRoomManagerReturn,
} from '../types';

export type {
  CallType,
  InternalCallType,
  WebCallResponse,
  SipParticipantState,
  AllParticipantStates,
  UseRoomManagerReturn,
};

export const useRoomManager = (
  agentId: string,
  campaignId: string,
  companyHash: string,
  onDisconnect: () => void,
  onBackendCallEnd: () => void,
  squadId?: string | null,
): UseRoomManagerReturn => {
  const { t } = useTranslation();
  const [room] = useState(new Room({ audioCaptureDefaults: CALLER_AUDIO_CAPTURE }));
  const [callType, setCallType] = useState<InternalCallType | null>(null);
  const [currentCallType, setCurrentCallType] = useState<CallType>(CALL_TYPES.WEB_CALL);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [callId, setCallId] = useState<string | null>(null);
  const [sipParticipantState, setSipParticipantState] =
    useState<SipParticipantState>(DEFAULT_SIP_STATE);
  const [participantStates, setParticipantStates] = useState<AllParticipantStates>(
    DEFAULT_PARTICIPANT_STATES,
  );
  const [wasUserInitiatedDisconnect, setWasUserInitiatedDisconnect] = useState<boolean>(false);
  const wasUserInitiatedDisconnectRef = useRef(wasUserInitiatedDisconnect);
  wasUserInitiatedDisconnectRef.current = wasUserInitiatedDisconnect;

  const onDisconnectRef = useRef(onDisconnect);
  onDisconnectRef.current = onDisconnect;

  const onBackendCallEndRef = useRef(onBackendCallEnd);
  onBackendCallEndRef.current = onBackendCallEnd;

  const [createCall, { error: createCallError }] =
    useCampaignTestCallCreateControllerTestCallCreateMutation();

  const isAgent = useCallback((participant: Participant): boolean => {
    return (
      participant.kind === ParticipantKind.AGENT &&
      participant.identity.startsWith(PARTICIPANT_IDENTITIES.AGENT_PREFIX)
    );
  }, []);

  const isCustomer = useCallback((participant: Participant): boolean => {
    return (
      participant.kind === ParticipantKind.SIP ||
      participant.identity === PARTICIPANT_IDENTITIES.CUSTOMER ||
      participant.identity.includes(PARTICIPANT_IDENTITIES.SIP_IDENTIFIER)
    );
  }, []);

  const updateAgentState = useCallback((connected: boolean, identity?: string) => {
    setParticipantStates((prev) => ({
      ...prev,
      agent: { connected, identity: connected ? identity : undefined },
    }));
  }, []);

  const updateCustomerState = useCallback(
    (connected: boolean, answered: boolean = false, identity?: string) => {
      setSipParticipantState({ connected, answered });
      setParticipantStates((prev) => ({
        ...prev,
        customer: { connected, answered, identity: connected ? identity : undefined },
      }));
    },
    [],
  );

  const handleCustomerAnswered = useCallback(() => {
    setSipParticipantState((prev) => ({ ...prev, answered: true }));
    setParticipantStates((prev) => ({
      ...prev,
      customer: { ...prev.customer, answered: true },
    }));
  }, []);

  const resetAllParticipantStates = useCallback(() => {
    setSipParticipantState(DEFAULT_SIP_STATE);
    setParticipantStates(DEFAULT_PARTICIPANT_STATES);
  }, []);

  const onDeviceFailure = useCallback(
    (error: Error) => {
      console.error(error);
      toast.error(
        (props) =>
          React.createElement(CustomErrorToast, {
            ...props,
            message: t('agentPage.errors.cameraPermissionError'),
          }),
        {
          closeButton: false,
          autoClose: 5000,
          icon: false,
        },
      );
    },
    [t],
  );

  const onConnectButtonClicked = useCallback(
    async (
      selectedCallType: CallType,
      selectedPhoneNumber?: string,
      attributes?: Record<string, unknown>,
    ) => {
      try {
        const { name, gender, ...restAttributes } = (attributes ?? {}) as {
          name?: string;
          gender?: 'male' | 'female';
          [key: string]: unknown;
        };

        const remainingAttributes =
          Object.keys(restAttributes).length > 0 ? restAttributes : undefined;

        const payload: CampaignTestCallCreateRequestDto = {
          callType: selectedCallType,
          ...(name && { name }),
          ...(gender && { gender }),
          ...(remainingAttributes && { attributes: remainingAttributes }),
          ...(squadId ? { squadId } : { agentId }),
        };

        if (selectedCallType === CALL_TYPES.OUTBOUND_PHONE_CALL && selectedPhoneNumber) {
          payload.phoneNumber = selectedPhoneNumber;
        }

        const result = await createCall({
          id: campaignId,
          campaignTestCallCreateRequestDto: payload,
        });

        if ('error' in result) {
          throw new Error(t('agentPage.errors.failedToCreateCall'));
        }

        const webCallResponse = result.data as WebCallResponse;

        if (!webCallResponse.token) {
          toast.error(
            (props) =>
              React.createElement(CustomErrorToast, {
                ...props,
                message: t('agentPage.errors.callTokenNotProvided'),
              }),
            {
              closeButton: false,
              autoClose: 5000,
              icon: false,
            },
          );
          return;
        }

        setCurrentCallType(selectedCallType);
        setPhoneNumber(selectedPhoneNumber || '');
        setCallId(webCallResponse.call?.id ?? null);

        const livekitServerUrl = resolveWebSocketUrl(VITE_LIVEKIT_HOST || DEFAULT_LIVEKIT_HOST);
        await room.connect(livekitServerUrl, webCallResponse.token);

        if (selectedCallType === CALL_TYPES.WEB_CALL) {
          await room.localParticipant.setMicrophoneEnabled(true);
          setCallType(INTERNAL_CALL_TYPES.WEB_CALL);
        } else {
          await room.localParticipant.setMicrophoneEnabled(false);
          setCallType(INTERNAL_CALL_TYPES.OUTBOUND_CALL);
        }
      } catch (error) {
        console.error('Failed to start call:', error);
        toast.error(
          (props) =>
            React.createElement(CustomErrorToast, {
              ...props,
              message: t('agentPage.errors.failedToConnectAgent'),
            }),
          {
            closeButton: false,
            autoClose: 5000,
            icon: false,
          },
        );
      }
    },
    [room, agentId, campaignId, companyHash, createCall, squadId, t],
  );

  const resetCallState = useCallback(() => {
    setCallType(null);
    setCurrentCallType(CALL_TYPES.WEB_CALL);
    setPhoneNumber('');
    setCallId(null);
    resetAllParticipantStates();
  }, [resetAllParticipantStates]);

  const handleDisconnect = useCallback(() => {
    setWasUserInitiatedDisconnect(true);
    if (room.state === ROOM_STATES.CONNECTED) {
      room.disconnect();
    }
    resetCallState();
    onDisconnectRef.current();
  }, [room, resetCallState]);

  const handleTrackSubscribed = useCallback(
    (_track: Track, _publication: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (isCustomer(participant)) {
        handleCustomerAnswered();
      }
    },
    [isCustomer, handleCustomerAnswered],
  );

  const handleParticipantConnected = useCallback(
    (participant: Participant) => {
      if (isAgent(participant)) {
        updateAgentState(true, participant.identity);
      } else if (isCustomer(participant)) {
        updateCustomerState(true, false, participant.identity);
        participant.on(ParticipantEvent.TrackPublished, handleCustomerAnswered);

        const hasSubscribedAudioTrack = Array.from(participant.trackPublications.values()).some(
          (pub) => pub.kind === Track.Kind.Audio && pub.track != null,
        );
        if (hasSubscribedAudioTrack) {
          handleCustomerAnswered();
        }
      }
    },
    [isAgent, isCustomer, updateAgentState, updateCustomerState, handleCustomerAnswered],
  );

  const handleParticipantDisconnected = useCallback(
    (participant: Participant) => {
      if (isAgent(participant)) {
        updateAgentState(false);
      } else if (isCustomer(participant)) {
        updateCustomerState(false);
      }
    },
    [isAgent, isCustomer, updateAgentState, updateCustomerState],
  );

  const handleRoomDisconnected = useCallback(() => {
    setTimeout(() => {
      resetCallState();
      if (wasUserInitiatedDisconnectRef.current) {
        onDisconnectRef.current();
      } else {
        onBackendCallEndRef.current();
      }
      setWasUserInitiatedDisconnect(false);
    }, TIMEOUTS.DISCONNECT_DELAY);
  }, [resetCallState]);

  useEffect(() => {
    room.on(RoomEvent.MediaDevicesError, onDeviceFailure);
    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
    room.on(RoomEvent.ParticipantAttributesChanged, (_state, participant) =>
      handleParticipantConnected(participant),
    );
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
    room.on(RoomEvent.Disconnected, handleRoomDisconnected);

    return () => {
      room.off(RoomEvent.MediaDevicesError, onDeviceFailure);
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
      room.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      room.off(RoomEvent.Disconnected, handleRoomDisconnected);
      if (room.state === ROOM_STATES.CONNECTED) {
        room.disconnect();
      }
    };
  }, [
    room,
    onDeviceFailure,
    handleParticipantConnected,
    handleParticipantDisconnected,
    handleTrackSubscribed,
    handleRoomDisconnected,
  ]);

  return {
    room,
    callType,
    currentCallType,
    phoneNumber,
    callId,
    onConnectButtonClicked,
    resetCallState,
    handleDisconnect,
    sipParticipantState,
    participantStates,
    createCallError,
  };
};
